import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { useEffect, useMemo, useRef, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { useThemeMotion } from '../../../animation/themeMotion'
import type { DynamicIslandActivity } from '../../../context/dynamicIslandTypes'
import { resolveDynamicIslandMotion } from '../../../lib/dynamicIsland/dynamicIslandMotion'
import { useTheme } from '../../../theme/ThemeContext'
import { ThemeDynamicIslandActivityView } from './ThemeDynamicIslandActivityView'
import {
  themeDynamicIslandAnchorClass,
  themeDynamicIslandFloaterClass,
  themeDynamicIslandShellClass,
} from './dynamicIslandTokens'
import { useDynamicIslandIdleCollapse } from './useDynamicIslandIdleCollapse'

type Props = {
  angular: boolean
  activities: DynamicIslandActivity[]
  selectedId: string | null
  onSelectId: (id: string) => void
  expanded: boolean
  setExpanded: (next: boolean) => void
  contentRevision: number
  detailModalOpen: boolean
}

export function ThemeDynamicIslandHost({
  angular,
  activities,
  selectedId,
  onSelectId,
  expanded,
  setExpanded,
  contentRevision,
  detailModalOpen,
}: Props) {
  const { theme } = useTheme()
  const glass = theme === 'glass'
  const motionPreset = useThemeMotion()
  const reduced = useReducedMotion()
  const islandMotion = resolveDynamicIslandMotion(motionPreset, !!reduced)
  const shellRef = useRef<HTMLDivElement>(null)

  const selected = useMemo(
    () => activities.find((item) => item.id === selectedId) ?? activities[0] ?? null,
    [activities, selectedId],
  )

  const activityLayoutKey = activities.map((item) => item.id).join('\0')

  useDynamicIslandIdleCollapse(
    shellRef,
    expanded && selected != null && !detailModalOpen,
    setExpanded,
    3000,
  )

  useEffect(() => {
    if (!expanded || !selected || detailModalOpen) return

    const onPointerDown = (e: PointerEvent) => {
      const el = shellRef.current
      if (!el || el.contains(e.target as Node)) return
      setExpanded(false)
    }

    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown, true)
    }, 0)

    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [expanded, selected, detailModalOpen, setExpanded])

  const scrimActive = expanded && selected?.scrimOnExpand
  const shellExtra = selected?.shellClassName ?? ''
  const soloLayout = activities.length === 1
  const multiStack = activities.length > 1

  const islandLayer = (
    <AnimatePresence>
      {selected ? (
        <div key="island-anchor" className={themeDynamicIslandAnchorClass()}>
          <motion.div
            role="status"
            aria-live="polite"
            className={themeDynamicIslandFloaterClass()}
            initial={{ opacity: 0, y: -islandMotion.enterY }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -islandMotion.enterY * 0.75 }}
            transition={islandMotion.reveal}
          >
            <div
              ref={shellRef}
              className={[
                themeDynamicIslandShellClass(angular, expanded, glass),
                soloLayout ? 'glyphicai-dynamic-island--single' : '',
                multiStack ? 'glyphicai-dynamic-island--multi-stack' : '',
                shellExtra,
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                ...islandMotion.cssVars,
                ...(multiStack || soloLayout
                  ? ({ '--island-category-count': activities.length } as CSSProperties)
                  : {}),
              }}
            >
              {contentRevision >= 0 ? (
                <ThemeDynamicIslandActivityView
                  key={activityLayoutKey}
                  activities={activities}
                  selectedId={selectedId}
                  onSelectId={onSelectId}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  angular={angular}
                />
              ) : null}
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  )

  const scrimLayer = (
    <AnimatePresence>
      {scrimActive ? (
        <motion.div
          key="dynamic-island-scrim"
          className="comic-episode-queue-island-scrim pointer-events-none fixed inset-0 z-[499]"
          aria-hidden
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={islandMotion.expand}
        />
      ) : null}
    </AnimatePresence>
  )

  const body = (
    <>
      {scrimLayer}
      {islandLayer}
    </>
  )

  if (typeof document === 'undefined') return body
  return createPortal(body, document.body)
}
